import { Dialog, DialogActions, DialogContent, FormControl, FormControlLabel, Grid, InputAdornment, InputLabel, MenuItem, Select, Switch, Typography } from '@mui/material';
import { MprcChipChoice, MprcConfirmDialog, MprcContainer, MprcDialogTitleClose, MprcRequestErrorDialog, MprcRequestIndicator } from 'components';
import { ButtonCancel, ButtonConfirm } from 'components/buttons';
import { MprcFilterBox } from 'components/filter';
import { IconAdd } from 'components/icons';
import { AutoCompleteDataModel } from 'components/MprcAutoComplete';
import { MprcRibbonScroll } from 'components/MprcRibbonScroll';
import { ManagedUserIdiomsResponse, UserIdiomAbroadExperienceTime, UserIdiomAbroadExperienceTimeSelectorResponse, UserIdiomProficiencyItem, UserIdiomProficiencyType, UserIdiomProficiencyTypesSelectorResponse } from 'generated/CoreApi_v1';
import LibUtils from 'lib/LibUtils';
import { useSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserIdiomsAutoComplete } from '../../components/UserIdiomsAutoComplete';

////////////////////////////////////////////////////////////////////////////

interface IProps {
    userId: string;
    userName: string;
    readonly?: boolean;
    onClose: () => void;
}

////////////////////////////////////////////////////////////////////////////

export const EditUserIdiomProficiency: React.FC<IProps> = (props) => {

    const { userId, userName, readonly = false, onClose } = props;
    const { t } = useTranslation();
    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();

    const [dialog, setDialog] = useState<any>();
    const [selectedItemIndex, setSelectedItemIndex] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [viewData, setViewData] = useState<ManagedUserIdiomsResponse[]>([]);
    const [loadDto, setLoadDto] = useState<ManagedUserIdiomsResponse[]>([]);
    const [viewError, setViewError] = useState<any>();
    const proficienceTypes = useRef<UserIdiomProficiencyTypesSelectorResponse[]>([]);
    const abrodExperienceTimes = useRef<UserIdiomAbroadExperienceTimeSelectorResponse[]>([]);

    const isRecordChanged = !LibUtils.deepCompare(viewData, loadDto);

    ////////////////////////////////////////////////////////////////////////////

    ///
    const handleNewItem = (items: AutoCompleteDataModel[]) => {
        if (items.length > 0) {
            if (viewData.filter(x => x.idiomId == items[0].key).length === 0) {

                setViewData([...viewData, {
                    idiomId: items[0].key,
                    name: items[0].label,
                    hearingProficiency: UserIdiomProficiencyType.Basic,
                    readingProficiency: UserIdiomProficiencyType.Basic,
                    speakingProficiency: UserIdiomProficiencyType.Basic,
                    writingProficiency: UserIdiomProficiencyType.Basic,
                    isNative: false,
                    abroadExperienceTime: null,
                    isDefault: false,
                    hasAbroadExperienceTime: false
                }]);

                setSelectedItemIndex(viewData.length);
            }
        }
    };

    ///
    const updateRecord = (idiomId: string, key: keyof ManagedUserIdiomsResponse, value: any) => {
        const newItems = viewData.map(item => {
            if (item.idiomId == idiomId) {

                if (key == "hasAbroadExperienceTime")
                    return { ...item, [key]: value, abroadExperienceTime: null };

                return { ...item, [key]: value };
            }
            return item;
        });
        setViewData([...newItems]);
    };

    ///
    const handleDeleteItem = (index: number, item: ManagedUserIdiomsResponse) => {
        setDialog(<MprcConfirmDialog
            open
            message={t("User.Edit.Idioms.ConfirmDeleteMessage")}
            onClose={yes => {
                if (yes) {
                    if (index == selectedItemIndex) setSelectedItemIndex(0);
                    setViewData([...viewData.filter(x => x.idiomId != item.idiomId)]);
                }
                setDialog(null);
            }}
        />);
    };

    ///
    const save = () => {
        setSaving(true);
        api.v1.userManagement.addOrUpdateIdioms(
            {
                userId,
                idioms: viewData.map(i => i as UserIdiomProficiencyItem)
            }
        )
            .then(result => {
                if (result.data) {
                    enqueueSnackbar(t("User.Edit.Idioms.SaveSuccessMessage"), { variant: "success" });
                    onClose();
                }
                else
                    enqueueSnackbar(t("User.Edit.Idioms.SaveErrorMessage"), { variant: "error" });
            })
            .catch(e => setViewError(e))
            .finally(() => setSaving(false));
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function getData() {
            try {
                const results = await Promise.all([
                    api.v1.userManagement.getIdioms({ UserId: userId }),
                    api.v1.selectors.userIdiomProficiencTypes(),
                    api.v1.selectors.abroadExperienceTime()
                ]);

                setViewData(results[0].data);
                setLoadDto(LibUtils.deepCopy(results[0].data));
                proficienceTypes.current = results[1].data;
                abrodExperienceTimes.current = results[2].data;
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }
        if (loading) getData();
    }, [loading]);

    ///
    const RenderProficiencySelect = useCallback(
        (label: string, value: UserIdiomProficiencyType, onChange: (value: UserIdiomProficiencyType) => void, readonly: boolean) =>
            <FormControl fullWidth>
                <InputLabel>{label}</InputLabel>
                <Select
                    label={label}
                    value={value}
                    onChange={e => onChange(e.target.value as UserIdiomProficiencyType)}
                    disabled={readonly}
                >
                    {proficienceTypes.current.map(item =>
                        <MenuItem key={item.type} value={item.type}>{item.name}</MenuItem>
                    )}
                </Select>
            </FormControl>
        , [proficienceTypes]);

    return (
        <>
            {dialog}
            <MprcRequestIndicator visible={loading || saving} type={loading ? "request" : "post"} />
            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(undefined)} />
            <Dialog
                open
                fullWidth
                maxWidth="sm"
            >
                <MprcDialogTitleClose
                    title={t("User.Edit.Idioms.Title")}
                    subtitle={userName}
                    onClose={onClose}
                    shrinkPadding
                />
                <DialogContent>
                    <MprcContainer>
                        {!readonly &&
                            <MprcFilterBox>
                                <UserIdiomsAutoComplete
                                    selectedItems={[]}
                                    onChange={handleNewItem}
                                    placeholder={t("User.Edit.Idioms.SelectIdiomPlaceholder")}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <IconAdd fontSize="inherit" />
                                        </InputAdornment>
                                    }
                                />
                            </MprcFilterBox>
                        }
                    </MprcContainer>

                    <MprcContainer>
                        <MprcRibbonScroll>
                            {viewData.map((item, index) =>
                                <MprcChipChoice
                                    key={item.idiomId}
                                    label={item.name}
                                    onClick={() => setSelectedItemIndex(index)}
                                    onDelete={item.isDefault || readonly ? undefined : () => handleDeleteItem(index, item)}
                                    selected={index == selectedItemIndex}
                                />)
                            }
                        </MprcRibbonScroll>
                    </MprcContainer>

                    {viewData.length > 0 &&
                        <MprcContainer>
                            <Typography variant="overline">{viewData[selectedItemIndex].name}</Typography>
                            <Grid container rowSpacing={1} columnSpacing={2} alignItems="flex-end">
                                <Grid size={{ xs: 6 }}>
                                    {RenderProficiencySelect(t("User.Edit.Idioms.ReadingLabel"), viewData[selectedItemIndex].readingProficiency, (value) => updateRecord(viewData[selectedItemIndex].idiomId, "readingProficiency", value), readonly)}
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    {RenderProficiencySelect(t("User.Edit.Idioms.WritingLabel"), viewData[selectedItemIndex].writingProficiency, (value) => updateRecord(viewData[selectedItemIndex].idiomId, "writingProficiency", value), readonly)}
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    {RenderProficiencySelect(t("User.Edit.Idioms.HearingLabel"), viewData[selectedItemIndex].hearingProficiency, (value) => updateRecord(viewData[selectedItemIndex].idiomId, "hearingProficiency", value), readonly)}
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    {RenderProficiencySelect(t("User.Edit.Idioms.SpeakingLabel"), viewData[selectedItemIndex].speakingProficiency, (value) => updateRecord(viewData[selectedItemIndex].idiomId, "speakingProficiency", value), readonly)}
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <FormControlLabel
                                        label={t("User.Edit.Idioms.IsNativeLabel")}
                                        disabled={readonly}
                                        control={
                                            <Switch
                                                checked={viewData[selectedItemIndex].isNative}
                                                onChange={(e) => updateRecord(viewData[selectedItemIndex].idiomId, "isNative", e.target.checked)}
                                            />
                                        }
                                    />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <FormControlLabel
                                        label={t("User.Edit.Idioms.HasAbroadExperienceTimeLabel")}
                                        disabled={readonly}
                                        control={
                                            <Switch
                                                checked={viewData[selectedItemIndex].hasAbroadExperienceTime}
                                                onChange={e => updateRecord(viewData[selectedItemIndex].idiomId, "hasAbroadExperienceTime", !viewData[selectedItemIndex].hasAbroadExperienceTime)}
                                            />
                                        }
                                    />
                                </Grid>
                                {viewData[selectedItemIndex].hasAbroadExperienceTime &&
                                    <Grid size={{ xs: 6 }}>
                                        <FormControl fullWidth>
                                            <InputLabel>{t("User.Edit.Idioms.AbroadExperienceTimeLabel")}</InputLabel>
                                            <Select
                                                label={t("User.Edit.Idioms.AbroadExperienceTimeLabel")}
                                                value={viewData[selectedItemIndex].abroadExperienceTime ?? ""}
                                                disabled={readonly}
                                                onChange={e => updateRecord(viewData[selectedItemIndex].idiomId, "abroadExperienceTime", e.target.value as UserIdiomAbroadExperienceTime)}
                                            >
                                                {abrodExperienceTimes.current.map(item =>
                                                    <MenuItem key={item.type} value={item.type}>{item.name}</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                }
                            </Grid>
                        </MprcContainer>
                    }
                </DialogContent>
                {!readonly &&
                    <DialogActions>
                        <ButtonCancel variant="contained" onClick={onClose} />
                        <ButtonConfirm
                            text={"Salvar"}
                            title={"Salvar"}
                            variant="contained"
                            disabled={!isRecordChanged || loading || saving}
                            onClick={save}
                        />
                    </DialogActions>
                }
            </Dialog>
        </>
    );
};
